/**
 * End-to-end encryption for the P2P transfer (WebCrypto, AES-256-GCM).
 *
 * The key is born in the SENDER and travels only inside the QR / copyable code —
 * never to the signaling server. Everything that passes through the server
 * (SDP/ICE) and everything that crosses the DataChannel (snapshot) rides in
 * AES-GCM envelopes with this key, with a per-purpose AAD: the server cannot
 * read NOR forge the signaling (it authenticates the peer: MITM impossible
 * without the QR key).
 *
 * Envelope format: base64( iv[12] || ciphertext+tag ).
 */

import { base64ToBytes, bytesToBase64, utf8Decode, utf8Encode } from "./encoding";

export const TRANSFER_KEY_BYTES = 32;
const IV_BYTES = 12;

/**
 * Copies the bytes into its own `ArrayBuffer`: avoids assertions to
 * `BufferSource` (views over `ArrayBufferLike` do not type cleanly against
 * WebCrypto).
 */
function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new ArrayBuffer(bytes.length);
  new Uint8Array(copy).set(bytes);
  return copy;
}

export function generateTransferKeyBytes(): Uint8Array {
  const bytes = new Uint8Array(TRANSFER_KEY_BYTES);
  crypto.getRandomValues(bytes);
  return bytes;
}

export async function importTransferKey(raw: Uint8Array): Promise<CryptoKey> {
  if (raw.length !== TRANSFER_KEY_BYTES) {
    throw new Error("Invalid transfer key length");
  }
  return crypto.subtle.importKey("raw", toArrayBuffer(raw), { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);
}

/**
 * Per-purpose AAD (additional authenticated data): an envelope encrypted for
 * "signal:sender" cannot be reused as a chunk or vice versa (anti-replay
 * across contexts), and the chunk index stays authenticated (anti-reorder).
 */
export async function encryptEnvelope(
  key: CryptoKey,
  plaintext: Uint8Array | string,
  aad: string,
): Promise<string> {
  const iv = new Uint8Array(IV_BYTES);
  crypto.getRandomValues(iv);
  const data = typeof plaintext === "string" ? utf8Encode(plaintext) : plaintext;
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: toArrayBuffer(utf8Encode(aad)) },
    key,
    toArrayBuffer(data),
  );
  const out = new Uint8Array(IV_BYTES + ciphertext.byteLength);
  out.set(iv, 0);
  out.set(new Uint8Array(ciphertext), IV_BYTES);
  return bytesToBase64(out);
}

export async function decryptEnvelope(
  key: CryptoKey,
  envelope: string,
  aad: string,
): Promise<Uint8Array> {
  const bytes = base64ToBytes(envelope);
  if (bytes.length <= IV_BYTES) {
    throw new Error("Envelope too short");
  }
  const iv = bytes.subarray(0, IV_BYTES);
  const ciphertext = bytes.subarray(IV_BYTES);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv), additionalData: toArrayBuffer(utf8Encode(aad)) },
    key,
    toArrayBuffer(ciphertext),
  );
  return new Uint8Array(plaintext);
}

export async function decryptEnvelopeText(
  key: CryptoKey,
  envelope: string,
  aad: string,
): Promise<string> {
  return utf8Decode(await decryptEnvelope(key, envelope, aad));
}

export async function sha256Base64(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", toArrayBuffer(bytes));
  return bytesToBase64(new Uint8Array(digest));
}
